/**
 * Canonical STR booking validation rules.
 *
 * Single source of truth shared by the client (src/lib/bookingValidator.ts,
 * imported via a relative path into this file) and the server-side
 * validate-booking Edge Function. Previously each runtime had its own copy
 * of these rules and they had already drifted — the client's "max nights"
 * check compared a single booking's nights against the strata's annual cap,
 * while the edge function correctly compared the host's cumulative nights
 * (used + this booking) against the cap. This module uses the cumulative
 * (correct) version everywhere.
 *
 * No Deno- or browser-specific APIs are used here, so the same file can be
 * imported unmodified from both a Vite client bundle and a Deno Edge Function.
 */

export interface StrataConfigLike {
  max_nights_per_year: number;
  min_stay_nights: number;
  max_guest_count: number;
  fee_type: "flat" | "percentage" | "per_night";
  fee_amount: number;
  approved_units: string[];
}

export interface BookingRuleInput {
  nights: number;
  guestCount: number;
  checkIn: string; // ISO date
  checkOut: string;
  unitNumber: string;
  hostStatus: string;
  hostExpiresAt?: string | null;
  hostNightsUsed: number;
  hostNightLimit: number;
  strataConfig: StrataConfigLike;
}

export interface RuleCheck {
  rule: string;
  passed: boolean;
  message: string;
}

export interface BookingRuleResult {
  isValid: boolean;
  checks: RuleCheck[];
  warnings: string[];
  blockedReason?: string;
  /** Calculated strata fee for this booking (in dollars); 0 if blocked. */
  fee: number;
}

function calculateFee(nights: number, config: StrataConfigLike): number {
  if (!config.fee_amount) return 0;
  switch (config.fee_type) {
    case "flat":
      return config.fee_amount;
    case "per_night":
      return config.fee_amount * nights;
    case "percentage":
      // Percentage of total stay cost isn't calculable without a nightly
      // rate; return the percentage itself as a marker — caller applies it.
      return config.fee_amount;
    default:
      return 0;
  }
}

export function evaluateBookingRules(input: BookingRuleInput): BookingRuleResult {
  const checks: RuleCheck[] = [];
  const warnings: string[] = [];
  const config = input.strataConfig;

  // 1. Date sanity: valid, parseable, check-out after check-in, and the
  //    declared night count matches the actual date difference.
  const checkInDate = new Date(input.checkIn);
  const checkOutDate = new Date(input.checkOut);
  if (isNaN(checkInDate.getTime()) || isNaN(checkOutDate.getTime())) {
    checks.push({ rule: "valid_dates", passed: false, message: "Invalid check-in or check-out date format." });
  } else if (checkOutDate <= checkInDate) {
    checks.push({ rule: "valid_dates", passed: false, message: "Check-out date must be after check-in date." });
  } else {
    const actualNights = Math.ceil((checkOutDate.getTime() - checkInDate.getTime()) / (1000 * 60 * 60 * 24));
    const matches = actualNights === input.nights;
    checks.push({
      rule: "valid_dates",
      passed: matches,
      message: matches
        ? "Declared nights match check-in/check-out dates"
        : `Declared nights (${input.nights}) do not match check-in/check-out dates (${actualNights} nights).`,
    });
  }

  // 2. Host approval status
  const status = input.hostStatus.toLowerCase();
  const hostApproved = status === "approved" || status === "active";
  checks.push({
    rule: "host_approved",
    passed: hostApproved,
    message: hostApproved
      ? "Host registration is approved"
      : status === "suspended"
        ? "Host registration is suspended. Bookings cannot be created."
        : status === "expired"
          ? "Host registration has expired. Please renew before creating bookings."
          : status === "pending"
            ? "Host registration is still pending approval."
            : `Host status "${input.hostStatus}" does not allow new bookings.`,
  });

  // 3. Host registration expiry vs booking start date
  const regNotExpired = !input.hostExpiresAt || new Date(input.hostExpiresAt) >= checkInDate;
  checks.push({
    rule: "registration_not_expired",
    passed: regNotExpired,
    message: regNotExpired
      ? "Host registration is valid for the booking period"
      : `Host registration expires ${input.hostExpiresAt}, before the check-in date.`,
  });

  // 4. Unit must be STR-approved by the strata (if the list is non-empty)
  const approvedUnits = config.approved_units ?? [];
  const unitApproved = approvedUnits.length === 0 || approvedUnits.includes(input.unitNumber);
  checks.push({
    rule: "unit_approved",
    passed: unitApproved,
    message: unitApproved
      ? `Unit ${input.unitNumber} is approved for short-term rentals`
      : `Unit ${input.unitNumber} is not approved for short-term rentals by the strata.`,
  });

  // 5. Minimum stay
  checks.push({
    rule: "minimum_stay",
    passed: input.nights >= config.min_stay_nights,
    message:
      input.nights >= config.min_stay_nights
        ? `Stay of ${input.nights} nights meets minimum of ${config.min_stay_nights}`
        : `Minimum stay is ${config.min_stay_nights} night(s). This booking is only ${input.nights} night(s).`,
  });

  // 6. Maximum guest count
  checks.push({
    rule: "guest_count_limit",
    passed: input.guestCount <= config.max_guest_count,
    message:
      input.guestCount <= config.max_guest_count
        ? `Guest count (${input.guestCount}) is within limit of ${config.max_guest_count}`
        : `Maximum ${config.max_guest_count} guests allowed. This booking has ${input.guestCount}.`,
  });

  // 7. Host's personal annual night limit (cumulative: used + this booking)
  const hostNightsAfter = input.hostNightsUsed + input.nights;
  const withinHostLimit = hostNightsAfter <= input.hostNightLimit;
  checks.push({
    rule: "annual_night_limit",
    passed: withinHostLimit,
    message: withinHostLimit
      ? `Night count (${input.nights}) is within remaining limit (${input.hostNightLimit - input.hostNightsUsed}/${input.hostNightLimit})`
      : `This booking would exceed the host's annual night limit of ${input.hostNightLimit}. ` +
        `The host has ${Math.max(0, input.hostNightLimit - input.hostNightsUsed)} night(s) remaining.`,
  });

  // 8. Strata's annual maximum (cumulative: used + this booking)
  const withinStrataMax = hostNightsAfter <= config.max_nights_per_year;
  checks.push({
    rule: "config_max_nights",
    passed: withinStrataMax,
    message: withinStrataMax
      ? `Cumulative nights (${hostNightsAfter}) are within the strata's annual maximum of ${config.max_nights_per_year}`
      : `This booking would exceed the strata's annual maximum of ${config.max_nights_per_year} nights. ` +
        `${Math.max(0, config.max_nights_per_year - input.hostNightsUsed)} night(s) remaining for the year.`,
  });

  const isValid = checks.every((c) => c.passed);

  // Non-blocking warning when a host is close to their limit
  const percentUsed = input.hostNightLimit > 0 ? (hostNightsAfter / input.hostNightLimit) * 100 : 0;
  if (isValid && percentUsed >= 90) {
    warnings.push(`After this booking the host will have used ${Math.round(percentUsed)}% of their annual night limit.`);
  }

  const failedChecks = checks.filter((c) => !c.passed);
  const blockedReason = failedChecks.map((c) => c.message).join("; ");

  return {
    isValid,
    checks,
    warnings,
    blockedReason: isValid ? undefined : blockedReason,
    fee: isValid ? calculateFee(input.nights, config) : 0,
  };
}

/** Calculate the fee for a booking based on strata config (used outside the validation flow, e.g. booking forms). */
export function calculateBookingFee(
  nights: number,
  baseAmount: number,
  config: StrataConfigLike
): { strataFee: number; totalFee: number } {
  let strataFee = 0;

  switch (config.fee_type) {
    case "flat":
      strataFee = config.fee_amount;
      break;
    case "percentage":
      strataFee = (baseAmount * config.fee_amount) / 100;
      break;
    case "per_night":
      strataFee = nights * config.fee_amount;
      break;
  }

  return {
    strataFee: Math.round(strataFee * 100) / 100,
    totalFee: Math.round((baseAmount + strataFee) * 100) / 100,
  };
}
